import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import GalleryPage from "@/pages/GalleryPage";
import OrderPage from "@/pages/OrderPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import PaymentPage from "@/pages/PaymentPage";
import ThankYouPage from "@/pages/ThankYouPage";
import ProcessPage from "@/pages/ProcessPage";
import EventsPage from "@/pages/EventsPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";

const Public = ({ children }) => <Layout>{children}</Layout>;

const SITE_URL = "https://artbyjasoncaricatures.com";

// Injects a self-referencing canonical + og:url that is dynamic per route.
function RouteMeta() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null; // don't index/canonicalize admin
  let path = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const url = SITE_URL + (path === "/" ? "/" : path);
  return (
    <Helmet>
      <link rel="canonical" href={url} />
      <meta property="og:url" content={url} />
    </Helmet>
  );
}

function Protected({ children }) {
  const { user, loading, logout } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-confetti font-display text-xl">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin) return (
    <div className="min-h-screen grid place-items-center bg-confetti px-5" data-testid="not-authorized">
      <div className="brut-card p-10 max-w-md text-center">
        <h1 className="font-display font-bold text-3xl">Not authorized</h1>
        <p className="text-ink/60 mt-2">This account ({user.email}) isn't the shop owner, so it can't access the dashboard.</p>
        <button onClick={logout} className="brut-btn bg-sun mt-6">Sign out & try another account</button>
      </div>
    </div>
  );
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Public><Home /></Public>} />
      <Route path="/gallery" element={<Public><GalleryPage /></Public>} />
      <Route path="/order" element={<Public><OrderPage /></Public>} />
      <Route path="/testimonials" element={<Public><TestimonialsPage /></Public>} />
      <Route path="/process" element={<Public><ProcessPage /></Public>} />
      <Route path="/events" element={<Public><EventsPage /></Public>} />
      <Route path="/payment/success" element={<Public><PaymentSuccess /></Public>} />
      <Route path="/payment/cancel" element={<Public><PaymentCancel /></Public>} />
      <Route path="/payment/:orderId" element={<Public><PaymentPage /></Public>} />
      <Route path="/thank-you/:orderId" element={<Public><ThankYouPage /></Public>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const blockImgMenu = (e) => { if (e.target && e.target.tagName === "IMG") e.preventDefault(); };
    const guardOn = () => document.body.classList.add("capture-guard");
    const guardOff = () => document.body.classList.remove("capture-guard");
    const onVisibility = () => { document.hidden ? guardOn() : guardOff(); };
    const onKey = async (e) => {
      // Windows PrintScreen: wipe whatever was captured to the clipboard
      if (e.key === "PrintScreen") {
        try { await navigator.clipboard.writeText(""); } catch (_) {}
        guardOn();
        setTimeout(guardOff, 800);
      }
    };
    document.addEventListener("contextmenu", blockImgMenu);
    document.addEventListener("dragstart", blockImgMenu);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", guardOn);
    window.addEventListener("focus", guardOff);
    window.addEventListener("keyup", onKey);
    return () => {
      document.removeEventListener("contextmenu", blockImgMenu);
      document.removeEventListener("dragstart", blockImgMenu);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", guardOn);
      window.removeEventListener("focus", guardOff);
      window.removeEventListener("keyup", onKey);
    };
  }, []);
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <Toaster position="top-center" richColors />
            <div className="capture-shield" aria-hidden="true">🎨 Protected artwork — please view on the site<br/>© Art by Jason Caricatures</div>
            <RouteMeta />
            <AppRouter />
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
